import React, { useState, useRef, useEffect } from 'react';

/**
 * Reusable filter bar with quick-toggle pills + multi-select dropdowns.
 *
 * Props:
 *   pills: [{ key, label }]                Single-select toggle row. activePill === pill.key.
 *   activePill: string                     Which pill is currently selected.
 *   onPillChange: (key) => void
 *   multiSelects: [{ key, label, options: [{value, label}], values: [string] }]
 *                                          Each multi-select renders as a dropdown with checkboxes.
 *   onMultiSelectChange: (key, values) => void
 *   onClearAll: () => void                 Optional handler for "Clear filters" button.
 */
export const FilterBar = ({
    pills = [],
    activePill,
    onPillChange,
    multiSelects = [],
    onMultiSelectChange,
    onClearAll,
}) => {
    const hasActiveFilters =
        (activePill && activePill !== 'all') ||
        multiSelects.some(ms => ms.values && ms.values.length > 0);

    return (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex flex-wrap items-center gap-3">
                {/* Quick filter pills */}
                {pills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {pills.map(pill => {
                            const isActive = activePill === pill.key;
                            return (
                                <button
                                    key={pill.key}
                                    onClick={() => onPillChange(pill.key)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                        isActive
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pill.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Multi-select dropdowns */}
                {multiSelects.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {multiSelects.map(ms => (
                            <MultiSelectDropdown
                                key={ms.key}
                                label={ms.label}
                                options={ms.options || []}
                                values={ms.values || []}
                                onChange={(newValues) => onMultiSelectChange(ms.key, newValues)}
                            />
                        ))}
                    </div>
                )}

                {/* Clear filters button */}
                {hasActiveFilters && onClearAll && (
                    <button
                        onClick={onClearAll}
                        className="ml-auto text-sm text-gray-600 hover:text-gray-900 underline"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* Active-filter chips */}
            {multiSelects.some(ms => ms.values?.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                    {multiSelects.flatMap(ms =>
                        (ms.values || []).map(val => {
                            const opt = (ms.options || []).find(o => o.value === val);
                            const label = opt ? opt.label : val;
                            return (
                                <span
                                    key={`${ms.key}-${val}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                    {ms.label}: {label}
                                    <button
                                        onClick={() => onMultiSelectChange(ms.key, ms.values.filter(v => v !== val))}
                                        className="ml-1 hover:text-blue-900"
                                        aria-label={`Remove ${label}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const MultiSelectDropdown = ({ label, options, values, onChange }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;

    const toggleValue = (value) => {
        if (values.includes(value)) {
            onChange(values.filter(v => v !== value));
        } else {
            onChange([...values, value]);
        }
    };

    const selectedCount = values.length;
    const buttonLabel = selectedCount === 0
        ? label
        : `${label} (${selectedCount})`;

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={() => setOpen(!open)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1 ${
                    selectedCount > 0
                        ? 'bg-blue-50 text-blue-700 border-blue-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
            >
                {buttonLabel}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="absolute z-20 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                    {options.length > 8 && (
                        <div className="p-2 border-b border-gray-100">
                            <input
                                type="text"
                                placeholder={`Search ${label.toLowerCase()}...`}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                autoFocus
                            />
                        </div>
                    )}
                    <div className="overflow-y-auto flex-1">
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">No matches</div>
                        ) : (
                            filteredOptions.map(opt => (
                                <label
                                    key={opt.value}
                                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={values.includes(opt.value)}
                                        onChange={() => toggleValue(opt.value)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-900">{opt.label}</span>
                                </label>
                            ))
                        )}
                    </div>
                    {selectedCount > 0 && (
                        <div className="p-2 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => onChange([])}
                                className="text-xs text-gray-600 hover:text-gray-900 underline"
                            >
                                Clear {label.toLowerCase()}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
